import { canAccessInnerCircle, canAccessOuterCircle } from '@whispers/sdk';
import { Link, Navigate } from 'react-router-dom';

import { useCards, useUnlockedCards } from '../api.js';
import { DeckCardImage } from '../components/DeckCardImage.js';
import { CardGridSkeleton, Skeleton } from '../components/Skeleton.js';
import { useAuth } from '../contexts/AuthContext.js';
import { useStoryblokPreview } from '../contexts/StoryblokPreviewContext.js';
import { getMembershipUrl } from '../utils/membership.js';

export const CardCollectionPage = () => {
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const { isPreviewMode } = useStoryblokPreview();
  const hasAccess = canAccessOuterCircle(user);

  const { data: cardsData, isLoading: cardsLoading } = useCards(
    { include: ['decks'] },
    { enabled: hasAccess || isPreviewMode },
  );

  // Only fetch unlocked cards if authenticated
  const { data: unlockedData, isLoading: unlockedLoading } = useUnlockedCards({
    enabled: isAuthenticated && hasAccess,
  });

  const cards = cardsData?.cards ?? [];
  const decks = (cardsData?.decks ?? []).toSorted((a, b) => a.name.localeCompare(b.name));
  const unlockedIds = new Set(unlockedData?.cards ?? []);

  const isLoading = authLoading || cardsLoading || (isAuthenticated ? unlockedLoading : false);

  if (!isPreviewMode && !authLoading && !hasAccess) {
    return <Navigate to={getMembershipUrl('outer_circle', '/card-collection')} replace />;
  }

  if (isLoading) {
    return (
      <>
        <Skeleton className="h-4 w-40" />
        <Skeleton className="h-2 w-full rounded-full" />
        <CardGridSkeleton count={24} />
      </>
    );
  }

  return (
    <>
      {decks.map((deck) => {
        const isKeepers = deck.slug === 'keepers-of-the-wild-lore';
        const canViewKeepers = canAccessInnerCircle(user);
        const deckCards = cards.filter((card) => card.deckId === deck.id);
        const unlockedCards = deckCards
          .filter((card) => unlockedIds.has(card.id))
          .toSorted((a, b) => a.name.localeCompare(b.name));
        const lockedCards = deckCards
          .filter((card) => !unlockedIds.has(card.id))
          .toSorted((a, b) => a.name.localeCompare(b.name));

        return (
          <section key={deck.id}>
            {isKeepers && !canViewKeepers ? (
              <>
                <h1 className="text-xl font-medium text-text">{deck.name}</h1>
                <p className="mt-2 text-sm text-text-secondary">
                  {unlockedCards.length} of {deckCards.length} cards unlocked
                </p>
                <div className="mt-4">
                  <p className="mb-3 text-sm text-text-secondary">
                    48 cards reserved for Inner Circle members.
                  </p>
                  <Link
                    to={getMembershipUrl('inner_circle', '/card-collection')}
                    className="inline-flex rounded-lg bg-button-solid-bg px-4 py-2 text-sm font-medium text-text-inverse hover:bg-button-solid-bg-hover"
                  >
                    Explore Inner Circle
                  </Link>
                </div>
              </>
            ) : (
              <details className="group" open={!isKeepers}>
                <summary className="cursor-pointer list-none">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <h1 className="text-xl font-medium text-text">{deck.name}</h1>
                      <p className="mt-2 text-sm text-text-secondary">
                        {unlockedCards.length} of {deckCards.length} cards unlocked
                      </p>
                    </div>
                    <span className="text-sm text-text-secondary group-open:hidden">
                      Show cards
                    </span>
                    <span className="hidden text-sm text-text-secondary group-open:inline">
                      Hide cards
                    </span>
                  </div>
                  <div className="mt-3 h-2 overflow-hidden rounded-full bg-surface-sunken">
                    <div
                      className="h-full bg-button-solid-bg transition-all"
                      style={{ width: `${(unlockedCards.length / deckCards.length) * 100}%` }}
                    />
                  </div>
                </summary>

                {unlockedCards.length > 0 && (
                  <div className="mt-6">
                    <h2 className="mb-3 text-sm font-medium uppercase tracking-wide text-disabled-text">
                      Unlocked
                    </h2>
                    <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
                      {unlockedCards.map((card) => (
                        <Link
                          key={card.id}
                          to={`/decks/${deck.slug}/cards/${card.slug}`}
                          className="group"
                        >
                          <DeckCardImage
                            className="transition-transform group-hover:scale-105"
                            slug={card.slug}
                            deckSlug={deck.slug}
                            name={card.name}
                          />
                          <p className="mt-2 truncate text-center text-xs text-text-secondary">
                            {card.name}
                          </p>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {lockedCards.length > 0 && (
                  <div className="mt-6">
                    <h2 className="mb-3 text-sm font-medium uppercase tracking-wide text-disabled-text">
                      Locked
                    </h2>
                    <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
                      {lockedCards.map((card) => (
                        <div key={card.id} className="opacity-50">
                          <DeckCardImage slug="back" name="Locked Card" deckSlug={deck.slug} />
                          <p className="mt-2 truncate text-center text-xs text-disabled-text">?</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </details>
            )}
          </section>
        );
      })}
    </>
  );
};
